import React, { useState, useEffect, useCallback, useRef } from "react";
import Box from "@amzn/meridian/box";
import Heading from "@amzn/meridian/heading";
import Text from "@amzn/meridian/text";
import Input from "@amzn/meridian/input";
import Select, { SelectOption } from "@amzn/meridian/select";
import DataTable, {
  DataTableBody,
  DataTableHeader,
  DataTableCell,
  DataTableRow,
  DataTableHead,
} from "@amzn/meridian/data-table";
import Expander from "@amzn/meridian/expander";
import Divider from "@amzn/meridian/divider";
import Row from "@amzn/meridian/row";
import Column from "@amzn/meridian/column";
import Tooltip from "@amzn/meridian/tooltip";
import Button from "@amzn/meridian/button";
import Icon from "@amzn/meridian/icon";
import infoTokens from "@amzn/meridian-tokens/base/icon/info";

// TODO: MOVE SVG IMAGES TO CDN
import rosterAndRatesImage from "../../assets/images/rosterAndRates.svg";

import { useAppDispatch, useAppSelector } from "../../store";
import { selectShiftPlanData } from "../../store/slices/shiftPlanSlice";
import { setNavigationEnabled } from "../../store/slices/navigationSlice";
import {
  updateCategoryOverride,
  updateRateOverride,
  updateTotalAttendanceOverride,
  clearAllOverrides,
  setRatesAndRoster,
  setRatesDataLoading,
  incrementRatesAndRosterRetryCount,
  setError,
} from "../../store/slices/rosterAndRatesSlice";
import { showError, showNotification } from "../../store/slices/errorSlice";

import { RosterData } from "./model";
import {
  verifyRoster,
  getRatesAndRoster,
} from "./api";
import RatesRosterLoader from "../../components/RatesRosterLoader/RatesRosterLoader";
import Footer from "../../components/Footer";
import { MAX_RETRIES, POLLING_INTERVAL_IN_MS } from "./constants";
import { CATEGORY_TOOLTIP_MAP } from "../../i18n/tooltips";
import { useNavigate } from "react-router-dom";
import { PlanStatus, ROUTES } from "../../utils/constants";
import RatesAndRosterHeader from "./components/RatesAndRosterHeader/RatesAndRosterHeaderContainer";

/**
 * Preferred department display order.
 * Departments not in this list will appear at the end in their original order.
 */
const DEPARTMENT_DISPLAY_ORDER: string[] = [
	"C-Returns Clean Decant Processed",
  "C-Returns Processed",
  "C-Returns Support",
  "C-Returns Stow",
  "AR Grading",
  "AR Grading Support",
  "AR Specialty Grading",
  "Stow",
];

/** Sort departments: known ones first in defined order, unknown ones at end alphabetically */
const sortDepartments = (departments: string[]): string[] => {
  return [...departments].sort((a, b) => {
    const indexA = DEPARTMENT_DISPLAY_ORDER.indexOf(a);
    const indexB = DEPARTMENT_DISPLAY_ORDER.indexOf(b);
    
    // Both known: sort by defined order
    if (indexA !== -1 && indexB !== -1) return indexA - indexB;
    // Only A known: A first
    if (indexA !== -1) return -1;
    // Only B known: B first
    if (indexB !== -1) return 1;
    // Neither known: alphabetical (new functions go to end)
    return a.localeCompare(b);
  });
};

/**
 * Props interface for RosterAndRates component
 */
interface RosterAndRatesProps {
  forceHideLoader?: boolean; // For testing purposes
  readOnly?: boolean; // When true, disables all override inputs (used in Review page)
}

/**
 * Validation errors interface
 */
interface ValidationErrors {
  totalAttendance: boolean;
  categories: Record<string, boolean>;
  rates: Record<string, boolean>;
}

/**
 * Total Attendance inline component (matches roster adjustment item layout)
 */
const TotalAttendanceItem: React.FC<{
  totalAttendance: RosterData["totalAttendance"];
  overrideReasonList: string[];
  hasError?: boolean;
  readOnly?: boolean;
  onReasonChange?: () => void;
  onOverrideChange?: (hasValue: boolean, hasReason: boolean) => void;
}> = ({ totalAttendance, overrideReasonList, hasError = false, readOnly = false, onReasonChange, onOverrideChange }) => {
  const dispatch = useAppDispatch();
  const totalAttendanceOverride = useAppSelector(
    (state) => state.rosterAndRates.totalAttendanceOverride
  );

  // Use Redux state or fallback to props data with null safety
  const currentOverride =
    totalAttendanceOverride?.override ?? undefined;
  const currentReason =
    totalAttendanceOverride?.overrideReason ??
    totalAttendance?.overrideReason ??
    "Low Attendance";
  const currentComment =
    totalAttendanceOverride?.overrideComment ??
    totalAttendance?.overrideComment ??
    "";

  const handleOverrideChange = (value: string) => {
    dispatch(
      updateTotalAttendanceOverride({
        field: "override",
        value: value,
      })
    );
    
    // Real-time validation
    if (onOverrideChange) {
      const hasValue = value !== "" && value !== null && value !== undefined;
      const hasReason = !!(totalAttendanceOverride?.overrideReason || totalAttendance?.overrideReason);
      onOverrideChange(hasValue, hasReason);
    }
  };

  const handleReasonChange = (value: string) => {
    dispatch(
      updateTotalAttendanceOverride({
        field: "overrideReason",
        value,
      })
    );
    // Clear error when reason is selected
    if (value && onReasonChange) {
      onReasonChange();
    }
  };

  const handleCommentChange = (value: string) => {
    dispatch(
      updateTotalAttendanceOverride({
        field: "overrideComment",
        value,
      })
    );
  };

  const [expanded, setExpanded] = useState(true);

  return (
    <Expander
      title="Total Attendance"
      type="section"
      open={expanded}
      onChange={setExpanded}
    >
      <Row
        className="attendance-content"
        spacing="400"
        alignmentVertical="center"
        alignmentHorizontal="start"
      >
        <Column
          spacing="200"
          alignmentHorizontal="start"
          minWidth={150}
        >
          <Text type="b300">Recommended</Text>
          <Text type="b300">{totalAttendance?.recommended ?? "N/A"}%</Text>
        </Column>
        <Column
          spacing="200"
          alignmentHorizontal="start"
          minWidth={150}
        >
          <Text type="b300">Override</Text>
          {readOnly ? (
            <Text type="b300">{currentOverride ?? '—'}</Text>
          ) : (
          <Input
            value={currentOverride}
            onChange={(e) => handleOverrideChange(e)}
            width={110}
            type="number"
          />
          )}
        </Column>
        {currentOverride && (
          <Column
            spacing="200"
            alignmentHorizontal="start"
            minWidth={200}
          >
            <Text className="override-reason-heading">Override Reason</Text>
            {readOnly ? (
              <Text type="b300">{currentReason || '—'}</Text>
            ) : (
            <Select
              value={currentReason || ""}
              onChange={(value) => handleReasonChange(value as string)}
              width={200}
              placeholder="Select reason"
              error={hasError && !currentReason}
              errorMessage={(hasError && !currentReason) && "Override reason is required"}
            >
              {overrideReasonList.map((reasonOption) => (
                <SelectOption
                  key={reasonOption}
                  value={reasonOption}
                  label={reasonOption}
                />
              ))}
            </Select>
            )}
          </Column>
        )}
        {currentOverride && (
          <Column
            spacing="200"
            alignmentHorizontal="start"
            minWidth={180}
          >
            <Text type="b300">Comment</Text>
            {readOnly ? (
              <Text type="b300">{currentComment || '—'}</Text>
            ) : (
            <Input
              value={currentComment || ""}
              onChange={(value) => handleCommentChange(value)}
              width={200}
              type="text"
              placeholder="Enter comment"
            />
            )}
          </Column>
        )}
      </Row>
    </Expander>
  );
};

/**
 * Roster and Rates component
 */
const RosterAndRates: React.FC<RosterAndRatesProps> = ({
  forceHideLoader = false,
  readOnly = false,
}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const categoryOverrides = useAppSelector(
    (state) => state.rosterAndRates.categoryOverrides
  );
  const rateOverrides = useAppSelector(
    (state) => state.rosterAndRates.rateOverrides
  );
  const totalAttendanceOverride = useAppSelector(
    (state) => state.rosterAndRates.totalAttendanceOverride
  );
  const ratesDataLoading = useAppSelector(
    (state) => state.rosterAndRates.ratesDataLoading
  );
  const ratesAndRoster = useAppSelector(
    (state) => state.rosterAndRates.ratesAndRoster
  );

  const retryCount: number = useAppSelector(
    (state) => state.rosterAndRates.retryCount
  );

  const shiftPlanData = useAppSelector(selectShiftPlanData);

  // Use ref to track retry count to avoid recreating callback
  const retryCountRef = useRef(retryCount);
  retryCountRef.current = retryCount;

  const [pollingIntervalId, setPollingIntervalId] =
    useState<NodeJS.Timeout | null>(null);

  // Local state for UI expand/collapse - all categories open by default
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
    new Set(["roster-adjustments"])
  );

  // Validation errors state
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({
    totalAttendance: false,
    categories: {},
    rates: {},
  });

  // Get all data from Redux state consistently
  const rosterData = ratesAndRoster?.rosterData || null;
  const displayCategories = rosterData?.categories || [];

  // Mapping from backend category names → short display label
  const CATEGORY_DISPLAY_MAP: Record<string, string> = {
    'VTO (Voluntary Time Off)':   'VTO',
    'VET (Voluntary Extra Time)': 'VET',
    'Amazon Ready':               'Amazon Ready',
    'Labor Share':                'Labor Share',
    'Miscellaneous Hours':        'Miscellaneous Hours',
  };

  /**
   * Check if a category is always positive (VET, VTO, Amazon Ready).
   * These don't use +/- signs or color coding, and override values cannot contain signs.
   */
  const isAlwaysPositiveCategory = (name: string): boolean => {
    const normalizedName = name.toLowerCase();
    return normalizedName.includes('voluntary time off') ||
      normalizedName.includes('voluntary extra time') ||
      normalizedName.includes('vto') ||
      normalizedName.includes('vet') ||
      normalizedName.includes('amazon ready');
  };

  /**
   * Get the display label for a category (short name if mapped, else original)
   */
  const getCategoryLabel = (name: string): string =>
    CATEGORY_DISPLAY_MAP[name] ?? name;

  /**
   * Get tooltip text for a category (if any). Looks up by the resolved display label.
   */
  const getCategoryTooltip = (name: string): string | undefined =>
    CATEGORY_TOOLTIP_MAP[getCategoryLabel(name)];

  /**
   * Get the original hours value for a category.
   * Uses category.associatesCount directly — the backend populates this field with hours.
   */
  const getCategoryOriginalHours = (category: (typeof displayCategories)[0]): number =>
    category.associatesCount || 0;

  // Initialize category expanders as open when data loads
  useEffect(() => {
    if (displayCategories.length > 0) {
      setExpandedCategories(prev => {
        const newSet = new Set(prev);
        displayCategories.forEach(cat => newSet.add(`cat-${cat.name}`));
        return newSet;
      });
    }
  }, [displayCategories]);


  // Polling logic for getRatesAndRoster API
  const fetchRatesAndRosterData = useCallback(async () => {
      try {
          dispatch(setRatesDataLoading(true));

          if (retryCountRef.current <  MAX_RETRIES) {
              dispatch(incrementRatesAndRosterRetryCount());

              const response = await getRatesAndRoster(
                  shiftPlanData.stationCode!,
                  shiftPlanData.selectedDate!,
                  shiftPlanData.selectedShift!.toUpperCase(), // Convert to uppercase (DAY, NIGHT)
                  shiftPlanData.planId!
              );

              dispatch(setRatesAndRoster(response));

              // PENDING_USER_INPUT / PENDING_APPROVAL / APPROVED / RECEIVED_USER_INPUT:
              // Data is ready – stop polling and show UI
              const successStatuses: PlanStatus[] = [
                  PlanStatus.PENDING_USER_INPUT,
                  PlanStatus.PENDING_APPROVAL,
                  PlanStatus.APPROVED,
                  PlanStatus.RECEIVED_USER_INPUT,
              ];
              if (successStatuses.includes(response.planStatus as PlanStatus)) {
                  dispatch(setRatesDataLoading(false));
                  return true; // Stop polling
              }

              // PENDING_CREATION: Plan is still being created, continue polling
              if (response.planStatus === PlanStatus.PENDING_CREATION) {
                  return false; // Continue polling
              }

              // FAILED / REJECTED / CANCELLED / CLOSED or any unexpected status: stop polling and show error
              dispatch(setRatesDataLoading(false));
              dispatch(showError({
                  title: "Failed to Load Rates and Roster",
                  message: "Unable to load the rates and roster data. Please try again later."
              }));
              return true; // Stop polling
          } else {
              console.error("Max retry count exceeded, plan Id: ", shiftPlanData.planId, " has errored out.");
              dispatch(setError("Max retries to fetch plan Inputs have exceeded for planId: " + shiftPlanData.planId +
                  ". Please contact Tech for further support."))
              return true;
          }
      } catch (error) {
          console.error("Error fetching rates and roster data:", error);
          return false; // Continue polling on error
      }
  }, [dispatch, shiftPlanData.stationCode, shiftPlanData.selectedDate, shiftPlanData.selectedShift, shiftPlanData.planId]);


// Start polling when component mounts and required data is available
  useEffect(() => {
    // Clear any existing polling interval
    if (pollingIntervalId) {
      clearInterval(pollingIntervalId);
      setPollingIntervalId(null);
    }

    if (!shiftPlanData.selectedDate || !shiftPlanData.selectedShift || !shiftPlanData.stationCode) {
      console.log("Missing required data for getRatesAndRoster API call");
      return;
    }

    const startPolling = async () => {
      // Initial call
      const shouldStop = await fetchRatesAndRosterData();
      
      if (!shouldStop) {
        // Set up polling interval (every 5 seconds)
        const intervalId = setInterval(async () => {
          const shouldStopPolling = await fetchRatesAndRosterData();
          if (shouldStopPolling) {
            clearInterval(intervalId);
            setPollingIntervalId(null);
          }
        }, POLLING_INTERVAL_IN_MS);
        
        setPollingIntervalId(intervalId);
      }
    };

    startPolling();

    // Cleanup interval on unmount
    return () => {
      if (pollingIntervalId) {
        clearInterval(pollingIntervalId);
      }
    };
  }, [shiftPlanData.selectedDate, shiftPlanData.selectedShift, shiftPlanData.stationCode, fetchRatesAndRosterData]);



  // Validate that all overrides have override reasons and set error state
  const validateAndSetErrors = (): boolean => {
    const errorMessages: string[] = [];
    const newErrors: ValidationErrors = {
      totalAttendance: false,
      categories: {},
      rates: {},
    };
    
    // Check Total Attendance Override
    if (totalAttendanceOverride?.override && !totalAttendanceOverride?.overrideReason) {
      newErrors.totalAttendance = true;
      errorMessages.push("Total Attendance override requires a reason");
    }
    
    // Check Category Overrides (Roster Adjustments)
    Object.entries(categoryOverrides).forEach(([categoryName, data]) => {
      if (data.override && !data.overrideReason) {
        newErrors.categories[categoryName] = true;
        errorMessages.push(`Roster Adjustment "${categoryName}" override requires a reason`);
      }
      // VET/VTO/Amazon Ready overrides must not contain + or - signs
      if (data.override && isAlwaysPositiveCategory(categoryName)) {
        const overrideStr = data.override.toString();
        if (overrideStr.includes('+') || overrideStr.includes('-')) {
          newErrors.categories[categoryName] = true;
          errorMessages.push(`"${categoryName}" override cannot contain + or - signs. Please enter a positive number only.`);
        }
      }
    });
    
    // Check Rate Overrides
    Object.entries(rateOverrides).forEach(([functionId, data]) => {
      if (data.override && !data.overrideReason) {
        newErrors.rates[functionId] = true;
        
        // Find the function name for better error message
        let functionRate = null;
        if (ratesAndRoster?.ratesData?.functionRatesByDepartment) {
          const allRates = Object.values(ratesAndRoster.ratesData.functionRatesByDepartment)
            .reduce((acc, rates) => acc.concat(rates), []);
          functionRate = allRates.find(rate => rate.functionId === functionId);
        }
        
        const functionName = functionRate?.functionName || `Function ID ${functionId}`;
        errorMessages.push(`Rate override for "${functionName}" requires a reason`);
      }
    });
    
    setValidationErrors(newErrors);
    
    if (errorMessages.length > 0) {
      const errorMessage = "Please fix the following input errors:\n" + 
        errorMessages.map((error, index) => `${index + 1}. ${error}`).join("\n");
      dispatch(showNotification({
        title: "Validation Required",
        message: errorMessage,
        type: "warning"
      }));
      return false;
    }
    
    return true;
  };

  // Handle clear overrides
  const handleClearOverrides = async () => {
    try {
      dispatch(clearAllOverrides());
      // Clear all validation errors
      setValidationErrors({
        totalAttendance: false,
        categories: {},
        rates: {},
      });
    } catch (error) {
      console.error("Error clearing overrides:", error);
    }
  };

  // Handle save all overrides and verify
  const handleSaveAndNext = async () => {
    // Validate overrides before saving and set error state
    const isValid = validateAndSetErrors();
    
    if (!isValid) {
      return; // Stop execution - error message already shown
    }

    try {
      await verifyRoster();

      // Navigate to next step
      dispatch(showNotification({
        title: "Roster Verified",
        message: "All overrides saved and roster verified successfully!",
        type: "success"
      }));
      navigate(ROUTES.SHIFT_PLAN_INBOUND_TRAILERS);
    } catch (error) {
      console.error("Error saving overrides or verifying roster:", error);
      dispatch(showError({
        title: "Save Error",
        message: "Error occurred while saving. Please try again."
      }));
    }
  };

  // Helper function to get current category override data
  const getCategoryOverrideData = (categoryName: string) => {
    const reduxData = categoryOverrides[categoryName];
    const originalData = rosterData?.categories.find(
      (c) => c.name === categoryName
    );

    return {
      override: reduxData?.override ?? null,
      overrideReason:
        reduxData?.overrideReason ?? originalData?.overrideReason ?? "",
      overrideComment:
        reduxData?.overrideComment ?? originalData?.overrideComment ?? "",
    };
  };

  // Handle category override input changes
  const handleCategoryOverrideChange = (
    categoryName: string,
    value: string
  ) => {
    const numericValue = value === "" ? null : value;
    dispatch(
      updateCategoryOverride({
        categoryName,
        field: "override",
        value: numericValue,
      })
    );
    
    // Real-time validation
    if (numericValue !== null) {
      // Override was entered - check if reason exists
      const currentReason = categoryOverrides[categoryName]?.overrideReason;
      if (!currentReason) {
        setValidationErrors(prev => ({
          ...prev,
          categories: { ...prev.categories, [categoryName]: true }
        }));
      }
    } else {
      // Override was cleared - remove error
      setValidationErrors(prev => ({
        ...prev,
        categories: { ...prev.categories, [categoryName]: false }
      }));
    }
  };

  const handleCategoryReasonChange = (categoryName: string, value: string) => {
    dispatch(
      updateCategoryOverride({
        categoryName,
        field: "overrideReason",
        value,
      })
    );
    // Clear error when reason is selected
    if (value) {
      setValidationErrors(prev => ({
        ...prev,
        categories: { ...prev.categories, [categoryName]: false }
      }));
    }
  };

  const handleCategoryCommentChange = (categoryName: string, value: string) => {
    dispatch(
      updateCategoryOverride({
        categoryName,
        field: "overrideComment",
        value,
      })
    );
  };

  // Helper function to get current rate override data
  const getRateOverrideData = (functionId: string) => {
    const reduxData = rateOverrides[functionId];

    return {
      override: reduxData?.override ?? null,
      overrideReason: reduxData?.overrideReason ?? "",
      overrideComment: reduxData?.overrideComment ?? "",
    };
  };

  // Handle rate override input changes
  const handleRateOverrideChange = (functionId: string, value: string) => {
    const numericValue = value === "" ? null : value;
    dispatch(
      updateRateOverride({
        functionId,
        field: "override",
        value: numericValue,
      })
    );
    
    // Real-time validation
    if (numericValue !== null) {
      // Override was entered - check if reason exists
      const currentReason = rateOverrides[functionId]?.overrideReason;
      if (!currentReason) {
        setValidationErrors(prev => ({
          ...prev,
          rates: { ...prev.rates, [functionId]: true }
        }));
      }
    } else {
      // Override was cleared - remove error
      setValidationErrors(prev => ({
        ...prev,
        rates: { ...prev.rates, [functionId]: false }
      }));
    }
  };

  const handleRateReasonChange = (functionId: string, value: string) => {
    dispatch(
      updateRateOverride({
        functionId,
        field: "overrideReason",
        value,
      })
    );
    // Clear error when reason is selected
    if (value) {
      setValidationErrors(prev => ({
        ...prev,
        rates: { ...prev.rates, [functionId]: false }
      }));
    }
  };

  const handleRateCommentChange = (functionId: string, value: string) => {
    dispatch(
      updateRateOverride({
        functionId,
        field: "overrideComment",
        value,
      })
    );
  };

  // Check if we should show full screen loader while polling for planStatus
  // Shows loader when: not force hidden AND (rates are loading and plan status is not PENDING_USER_INPUT or PENDING_CREATION)
  let shouldShowFullScreenLoader: boolean;

  if (forceHideLoader) {
    shouldShowFullScreenLoader = false;
  } else if (
    ratesDataLoading &&
    (!ratesAndRoster ||
      (ratesAndRoster.planStatus !== PlanStatus.PENDING_USER_INPUT))
  ) {
    shouldShowFullScreenLoader = true;
  } else if (!ratesAndRoster) {
    shouldShowFullScreenLoader = true;
  } else {
    shouldShowFullScreenLoader = false;
  }

  // Navigation control - disable navigation during loading states
  useEffect(() => {
    if (shouldShowFullScreenLoader) {
      dispatch(setNavigationEnabled(false));
    } else {
      dispatch(setNavigationEnabled(true));
    }

    // Cleanup: ensure navigation is enabled when component unmounts
    return () => {
      dispatch(setNavigationEnabled(true));
    };
  }, [shouldShowFullScreenLoader, dispatch]);

  return (
    <Box spacingInset="300" className="roster-and-rates">
      {shouldShowFullScreenLoader ? (
        <RatesRosterLoader type="roster" status="loading" />
      ) : rosterData ? (
        <>
          <div className="header">
            <div className="title-section" style={{ flex: 1 }}>
              <RatesAndRosterHeader />
            </div>
            <div className="illustration">
              <img src={rosterAndRatesImage} alt="Roster and Rates Illustration" />
            </div>
          </div>

          {/* Labor Adjustments Section (merged Total Attendance + Roster Adjustments) */}
          <Expander
            title="Labor Adjustments"
            type="section"
            open={expandedCategories.has("roster-adjustments")}
            onChange={(isOpen) => {
              if (isOpen) {
                setExpandedCategories(
                  (prev) => new Set([...Array.from(prev), "roster-adjustments"])
                );
              } else {
                setExpandedCategories((prev) => {
                  const newSet = new Set(prev);
                  newSet.delete("roster-adjustments");
                  return newSet;
                });
              }
            }}
          >
            <Box className="roster-adjustments-content">
              {/* Total Attendance sub-section */}
              <TotalAttendanceItem
                totalAttendance={rosterData.totalAttendance}
                overrideReasonList={["Weather", "Special Event", "Other"]}
                hasError={validationErrors.totalAttendance}
                readOnly={readOnly}
                onReasonChange={() => setValidationErrors(prev => ({ ...prev, totalAttendance: false }))}
                onOverrideChange={(hasValue: boolean, hasReason: boolean) => {
                  if (hasValue && !hasReason) {
                    setValidationErrors(prev => ({ ...prev, totalAttendance: true }));
                  } else if (!hasValue) {
                    setValidationErrors(prev => ({ ...prev, totalAttendance: false }));
                  }
                }}
              />

              <Divider />

              {/* Roster Adjustments sub-section */}
              {[...displayCategories]
                .sort((a, b) => a.order - b.order)
                .map((category, index) => {
                  // Use availableLabor hours if available, else fall back to associatesCount
                  const hoursValue = getCategoryOriginalHours(category);
                  const isAddition = hoursValue >= 0;

                  const overrideData = getCategoryOverrideData(category.name);

                  return (
                    <React.Fragment key={category.name}>
                    {index > 0 && <Divider />}
                    <Expander
                      title={getCategoryLabel(category.name)}
                      type="section"
                      open={expandedCategories.has(`cat-${category.name}`)}
                      onChange={(isOpen) => {
                        if (isOpen) {
                          setExpandedCategories(prev => new Set([...Array.from(prev), `cat-${category.name}`]));
                        } else {
                          setExpandedCategories(prev => {
                            const newSet = new Set(prev);
                            newSet.delete(`cat-${category.name}`);
                            return newSet;
                          });
                        }
                      }}
                    >
                      <Row
                        className="attendance-content"
                        spacing="400"
                        alignmentVertical="center"
                        alignmentHorizontal="start"
                      >
                        <Column
                          spacing="200"
                          alignmentHorizontal="start"
                          minWidth={150}
                        >
                          <Row
                            spacing="100"
                            alignmentVertical="center"
                            alignmentHorizontal="start"
                          >
                            <Text type="b300">Available Hours</Text>
                            {getCategoryTooltip(category.name) && (
                              <Tooltip
                                title={getCategoryTooltip(category.name)!}
                                position="top"
                                maxWidth={280}
                              >
                                <Button type="icon" size="small" label={`Info: ${getCategoryLabel(category.name)}`}>
                                  <Icon tokens={infoTokens} />
                                </Button>
                              </Tooltip>
                            )}
                          </Row>
                          <Box className="flex-row">
                            <span
                              style={{
                                color: isAlwaysPositiveCategory(category.name)
                                  ? "inherit"
                                  : isAddition ? "#067A6F" : "#D13636",
                                fontWeight: "bold",
                                fontSize: "16px",
                                marginRight: "8px",
                              }}
                            >
                              {isAlwaysPositiveCategory(category.name)
                                ? hoursValue
                                : `${isAddition ? "+" : "-"}${Math.abs(hoursValue)}`}
                            </span>
                            <Text type="b300">
                              {new Set(["Labor Share", "Miscellaneous Hours"]).has(getCategoryLabel(category.name))
                                ? "Hours Planned"
                                : "Hours Accepted"}
                            </Text>
                          </Box>
                        </Column>
                        <Column
                          spacing="200"
                          alignmentHorizontal="start"
                          minWidth={150}
                        >
                          <Text type="b300">Override</Text>
                          {readOnly ? (
                            <Text type="b300">{overrideData?.override?.toString() || '—'}</Text>
                          ) : (
                          <Input
                            value={overrideData?.override?.toString() || ""}
                            onChange={(value) =>
                              handleCategoryOverrideChange(category.name, value)
                            }
                            width={110}
                            type="number"
                          />
                          )}
                        </Column>
                        {overrideData?.override && (
                          <Column
                            spacing="200"
                            alignmentHorizontal="start"
                            minWidth={200}
                          >
                            <Text className="override-reason-heading">
                              Override Reason
                            </Text>
                            <Select
                              value={
                                  getCategoryOverrideData(category.name)
                                      .overrideReason || ""
                              }
                              onChange={(value) =>
                                  handleCategoryReasonChange(
                                      category.name,
                                      value as string
                                  )
                              }
                              width={200}
                              placeholder="Select reason"
                              error={validationErrors.categories[category.name] && !getCategoryOverrideData(category.name).overrideReason}
                              errorMessage={(validationErrors.categories[category.name] && !getCategoryOverrideData(category.name).overrideReason)
                                  && "Override reason is required"}
                            >
                              {(rosterData.overrideReasonList.length > 0
                                ? rosterData.overrideReasonList
                                : ["Learning/Veteran Curve", "Cube/Freight Mix projected", "Other"]).map(
                                (reasonOption) => (
                                    <SelectOption
                                        key={reasonOption}
                                        value={reasonOption}
                                        label={reasonOption}
                                    />
                                )
                              )}
                            </Select>
                          </Column>
                        )}
                        {overrideData?.override && (
                          <Column
                            spacing="200"
                            alignmentHorizontal="start"
                            minWidth={180}
                          >
                            <Text type="b300">Comment</Text>
                            <Input
                              value={
                                  getCategoryOverrideData(category.name)
                                      .overrideComment || ""
                              }
                              onChange={(value) =>
                                  handleCategoryCommentChange(
                                      category.name,
                                      value
                                  )
                              }
                              width={200}
                              type="text"
                              placeholder="Enter comment"
                            />
                          </Column>
                        )}
                      </Row>
                    </Expander>
                    </React.Fragment>
                  );
                })}
            </Box>
          </Expander>

          {/* Rates Section */}
          {ratesAndRoster?.ratesData && (
            <Expander
              title="Rates"
              type="section"
              open={expandedCategories.has("rates")}
              onChange={(isOpen) => {
                if (isOpen) {
                  setExpandedCategories(
                    (prev) => new Set([...Array.from(prev), "rates"])
                  );
                } else {
                  setExpandedCategories((prev) => {
                    const newSet = new Set(prev);
                    newSet.delete("rates");
                    return newSet;
                  });
                }
              }}
            >
              <Box className="rates-content">
                {sortDepartments(Object.keys(
                  ratesAndRoster.ratesData.functionRatesByDepartment || {}
                )).map((department) => (
                  <Box
                    key={department}
                    className="department-section"
                    type="outline"
                    spacingInset="300"
                  >
                    <Heading level={5}>{department}</Heading>

                    <DataTable>
                      <DataTableHead>
                        <DataTableRow>
                          <DataTableHeader width={220}>Function</DataTableHeader>
                          <DataTableHeader width={160}>FCLM Codes</DataTableHeader>
                          <DataTableHeader width={110}>Rate (UPH)</DataTableHeader>
                          <DataTableHeader width={110}>Override</DataTableHeader>
                          <DataTableHeader width={220}>Override Reason</DataTableHeader>
                          <DataTableHeader width={200}>Comment</DataTableHeader>
                        </DataTableRow>
                      </DataTableHead>
                      <DataTableBody>
                        {ratesAndRoster.ratesData.functionRatesByDepartment[
                          department
                        ].filter(
                          (functionRate) =>
                            !(
                              department === "AR Grading" &&
                              functionRate.functionId === "1667843456854"
                            )
                        ).map((functionRate) => (
                          <DataTableRow key={functionRate.functionId}>
                            <DataTableCell>
                              <Text type="b300">
                                {functionRate.functionName}
                              </Text>
                            </DataTableCell>
                            <DataTableCell>
                              <Text type="b300">
                                {functionRate.functionId}
                              </Text>
                            </DataTableCell>
                            <DataTableCell>
                              <Text type="b300">
                                {parseFloat(
                                  functionRate.rate.toString()
                                ).toFixed(1)}
                              </Text>
                            </DataTableCell>
                            <DataTableCell>
                              {readOnly ? (
                                <Text type="b300">{getRateOverrideData(functionRate.functionId)?.override?.toString() || '—'}</Text>
                              ) : (
                              <Input
                                value={getRateOverrideData(
                                  functionRate.functionId
                                )?.override?.toString() || ""}
                                onChange={(value: string) =>
                                  handleRateOverrideChange(
                                    functionRate.functionId,
                                    value
                                  )
                                }
                                width={90}
                                type="number"
                                placeholder="—"
                              />
                              )}
                            </DataTableCell>
                            <DataTableCell>
                              {getRateOverrideData(functionRate.functionId)?.override ? (
                                readOnly ? (
                                  <Text type="b300">{getRateOverrideData(functionRate.functionId).overrideReason || '—'}</Text>
                                ) : (
                                <Select
                                  value={
                                    getRateOverrideData(
                                      functionRate.functionId
                                    ).overrideReason as string
                                  }
                                  onChange={(value: string) =>
                                    handleRateReasonChange(
                                      functionRate.functionId,
                                      value
                                    )
                                  }
                                  width={200}
                                  placeholder="Select reason"
                                  error={validationErrors.rates[functionRate.functionId] && !getRateOverrideData(functionRate.functionId).overrideReason}
                                  errorMessage={(validationErrors.rates[functionRate.functionId] && !getRateOverrideData(functionRate.functionId).overrideReason)
                                      ? "Required" : undefined}
                                >
                                  {(
                                    rosterData.overrideReasonList.length > 0
                                    ? rosterData.overrideReasonList
                                        : ["Learning/Veteran Curve", "Cube/Freight Mix projected", "Other"]
                                  ).map((reasonOption) => (
                                    <SelectOption
                                      key={reasonOption}
                                      value={reasonOption}
                                      label={reasonOption}
                                    />
                                  ))}
                                </Select>
                                )
                              ) : (
                                <Text type="b300" color="secondary">—</Text>
                              )}
                            </DataTableCell>
                            <DataTableCell>
                              {getRateOverrideData(functionRate.functionId)?.override ? (
                                readOnly ? (
                                  <Text type="b300">{getRateOverrideData(functionRate.functionId).overrideComment || '—'}</Text>
                                ) : (
                                <Input
                                  value={
                                    getRateOverrideData(
                                      functionRate.functionId
                                    ).overrideComment as string
                                  }
                                  onChange={(value: string) =>
                                    handleRateCommentChange(
                                      functionRate.functionId,
                                      value
                                    )
                                  }
                                  width={180}
                                  type="text"
                                  placeholder="Comment"
                                />
                                )
                              ) : (
                                <Text type="b300" color="secondary">—</Text>
                              )}
                            </DataTableCell>
                          </DataTableRow>
                        ))}
                      </DataTableBody>
                    </DataTable>

                    {/* Department rates using DataTable */}
                  </Box>
                ))}
              </Box>
            </Expander>
          )}

          {/* Hidden: Available Associates by Permissions - as requested */}
          {/* 
          <Box style={{ display: 'none' }}>
            <Text>Available Associates by Permissions</Text>
            ... existing code hidden ...
          </Box>
          */}
          {/* ***FOOTER***  */}
          {!readOnly && (
            <Footer
              onClearOverrides={handleClearOverrides}
              onNext={handleSaveAndNext}
            />
          )}
        </>
      ) : (
        <div>No roster data available</div>
      )}
    </Box>
  );
};

export default RosterAndRates;

import './index.scss';